<template>
  <div>
    <v-menu
      max-width="290"
      min-width="290"
      :close-on-content-click="false"
      :value="isMenuOpened"
      rounded="0"
      offset-y
      v-if="!singleDate"
    >
      <template v-slot:activator="{ on }">
        <v-text-field
          :label="label ? label : $t('filters.dates')"
          @click="isMenuOpened = true"
          v-model="$store.getters.datesValue[startDateProp]"
          v-on="on"
          hide-details
          readonly
          outlined
          dense
        />
      </template>
      <v-list>
        <v-list-item
          class="filters__defaultFont"
          v-for="date in dateOptions"
          :key="date.title"
          @click="() => handleSelectDates(date.title, date.days)"
        >
          {{ $t(`filters.date.${[date.title]}`) }}
        </v-list-item>

        <div v-if="dateRange">
          <v-divider />
          <v-list-group append-icon="mdi-chevron-down">
            <template v-slot:activator no-action>
              <v-list-item-title class="filters__defaultFont">
                {{ $t('filters.date.customRange') }}
              </v-list-item-title>
            </template>
            <v-list-item>
              <FilterDatesRange
                :fetchData="fetchData"
                :startDateProp="startDateProp"
                :endDateProp="endDateProp"
                :closeOnClick="true"
                @change="emitDates"
              />
            </v-list-item>
          </v-list-group>
        </div>
      </v-list>
    </v-menu>

    <FilterSingleDate
      :fetchData="fetchData"
      :label="label"
      :singleDateProp="singleDateProp"
      v-if="singleDate"
    />
  </div>
</template>

<script>
import store from '@/store'
import moment from 'moment-timezone'
import { mapGetters, mapMutations } from 'vuex'

import FilterDatesRange from './FilterDatesRange'
import FilterSingleDate from './FilterSingleDate'

import { dateOptions, getDaysByTitle } from './FilterDatesOptions'

export default {
  name: 'FilterDates',

  props: {
    fetchData: {
      type: Function,
      required: true,
    },

    dateRange: {
      type: Boolean,
      default: false,
    },

    label: {
      type: String,
      default: '',
    },

    startDateProp: {
      type: String,
      default: 'startDate',
    },

    endDateProp: {
      type: String,
      default: 'endDate',
    },

    singleDateProp: {
      type: String,
      default: 'date',
    },

    singleDate: {
      type: Boolean,
      default: false,
    },

    defaultRange: {
      type: 'anyTime' | 'olderThanAWeek' | 'olderThanAMonth' | 'olderThanSixMonths' | 'olderThanOneYear' | 'recent',
      default: null,
      required: false
    },
  },

  components: {
    FilterDatesRange,
    FilterSingleDate
  },

  data() {
    return {
      timezone: moment.tz.guess(),
      isMenuOpened: false,
      dateOptions: dateOptions,
      params: {},
    }
  },

  computed: {
    ...mapGetters(['filters']),
  },

  methods: {
    ...mapMutations(['setFilters', 'setDatesValue', 'setCurrentPage']),

    emitDates() {
      this.$emit('input', { start: this.params[this.startDateProp] ?? '---', end: this.params[this.endDateProp] ?? '---' })
    },

    applyFilter() {
      this.isMenuOpened = false

      Promise
        .resolve(this.setFilters({ ...store.getters.filters, ...this.params }))
        .then(() => this.setCurrentPage(1))
        .then(() => this.fetchData())
        .then(() => this.$emit('input', { start: this.params[this.startDateProp] ?? '---', end: this.params[this.endDateProp] ?? '---' }))
    },

    handleSelectDates(title, days) {
      if (title === 'recent') {
        this.setRecentDateRange()
        return
      }

      this.params[this.endDateProp] = days
        ? this.formatDate()
        : ''

        this.params[this.startDateProp] = days
        ? this.formatDate(days)
        : ''

      this.setDatesValue({
        ...store.getters.datesValue,
        ...{ [this.startDateProp]: this.$t(`filters.date.${[title]}`) },
      })

      this.applyFilter()
    },

    setDefaultDateRange() {
      if (!this.defaultRange) return

      const days = getDaysByTitle(this.defaultRange)

      if (this.defaultRange === 'recent') {
        this.setRecentDateRange()
        return
      }

      this.params[this.endDateProp] = days
        ? this.formatDate()
        : ''

      this.params[this.startDateProp] = days
        ? this.formatDate(days)
        : ''

      this.setDatesValue({
        ...store.getters.datesValue,
        ...{ [this.startDateProp]: this.$t(`filters.date.${this.defaultRange}`) },
      })

      this.applyFilter()
    },

    setRecentDateRange() {
      // get the recent date range (from 1 month ago to 1 month from now)
      const startDate = moment().add(-1, 'month').format('YYYY-MM-DD') + 'T00:00:01Z'
      const endDate = moment().add(1, 'month').format('YYYY-MM-DD') + 'T23:59:59Z'

      this.params[this.endDateProp] = endDate
      this.params[this.startDateProp] = startDate

      this.setDatesValue({
        ...store.getters.datesValue,
        ...{ [this.startDateProp]: this.$t('filters.date.recent'), },
      })

      this.setFilters({ ...store.getters.filters, ...{ [this.startDateProp]: startDate, [this.endDateProp]: endDate } })

      this.applyFilter()
    },

    formatDate(days) {
      // documentation for UTC offset
      // https://momentjscom.readthedocs.io/en/latest/moment/03-manipulating/09-utc-offset/

      const dateString = moment().subtract(days ?? 0, 'days').format(`YYYY-MM-DDT${days ? '00:00:01' : '23:59:59'}Z`)
      const dateMoment = moment(dateString)
      const newDateMoment = dateMoment.clone().subtract(moment.tz.zone(this.timezone).utcOffset(dateMoment), 'minutes')
      const newDateString = newDateMoment.format('YYYY-MM-DDTHH:mm:ss')

      return newDateString + 'Z'
    },
  },

  mounted() {
    this.setDefaultDateRange()
  },
}
</script>
